import React from "react";
import { LuPaperclip } from "react-icons/lu";
import { Badge, Button, Field, SegmentedControl, Textarea } from "../../../components/ui";
import { t } from "../../../i18n";
import { showError } from "../../../lib/feedback";
import { MAX_UPLOAD_BYTES } from "../../../lib/validators";
import { ItemRecurso, NegativaFonte } from "../../../types/resources.types";
import { OptionCard } from "../OptionCard";
import { AppealWizard } from "../useAppealWizard";

const FILE_ID = "appeal-denial-file";

/** Passo 2: a negativa da operadora (texto ou arquivo, opcional) e os itens recusados. */
export const DeniedItemsStep: React.FC<{ w: AppealWizard }> = ({ w }) => {
  const group = (title: string, items: ItemRecurso[], code: boolean) => (
    <section className="flex flex-col gap-2.5">
      <h3 className="flex items-center gap-2 text-body font-semibold text-fg-secondary">
        {title}
        <Badge tone="primary">
          {items.filter((i) => w.itemIds.includes(i.id)).length}/{items.length}
        </Badge>
      </h3>
      {items.length === 0 ? (
        <p className="text-caption text-fg-muted">{t("appeals.items.empty")}</p>
      ) : (
        items.map((item) => (
          <OptionCard
            key={item.id}
            selected={w.itemIds.includes(item.id)}
            onClick={() => w.toggleItem(item.id)}
            title={code ? item.id : item.descricao}
            description={code ? item.descricao : undefined}
          />
        ))
      )}
    </section>
  );

  return (
    <>
      <p className="text-body text-fg-muted">{t("appeals.items.intro")}</p>

      <section className="flex flex-col gap-3 rounded-card border border-line bg-surface-muted p-4">
        <div>
          <h3 className="text-body font-semibold">{t("appeals.items.denial")}</h3>
          <p className="text-caption text-fg-muted">{t("appeals.items.denialHint")}</p>
        </div>
        <SegmentedControl<NegativaFonte>
          aria-label={t("appeals.items.denialSource")}
          tone="soft"
          value={w.denialSource}
          onChange={w.setDenialSource}
          options={[
            { value: "texto", label: t("appeals.items.text") },
            { value: "arquivo", label: t("appeals.items.file") },
          ]}
        />
        {w.denialSource === "texto" ? (
          <Field label={t("appeals.items.textLabel")}>
            <Textarea
              rows={5}
              value={w.denialText}
              onChange={(e) => w.setDenialText(e.target.value)}
              placeholder={t("appeals.items.textPlaceholder")}
            />
          </Field>
        ) : (
          <>
            <input
              id={FILE_ID}
              type="file"
              accept=".pdf,image/*"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                e.target.value = "";
                if (file && file.size > MAX_UPLOAD_BYTES) return showError("file-too-large");
                w.setDenialFile(file);
              }}
            />
            {w.denialFile ? (
              <div className="flex flex-wrap items-center gap-3 rounded-control border border-line bg-surface px-3.5 py-2.5">
                <LuPaperclip size={18} className="shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1">
                  <strong className="block truncate text-body font-semibold">{w.denialFile.name}</strong>
                  <span className="text-caption text-fg-muted">{(w.denialFile.size / 1024).toFixed(1)} KB</span>
                </span>
                <label
                  htmlFor={FILE_ID}
                  className="cursor-pointer rounded-button px-2.5 py-2 text-label font-semibold text-primary hover:bg-primary-tint"
                >
                  {t("appeals.items.fileChange")}
                </label>
                <Button variant="text" size="sm" onClick={() => w.setDenialFile(null)}>
                  {t("appeals.items.fileRemove")}
                </Button>
              </div>
            ) : (
              <label
                htmlFor={FILE_ID}
                className="flex cursor-pointer flex-col items-center gap-1 rounded-card border-2 border-dashed border-primary-line bg-primary-tint px-4 py-5 text-center text-fg-muted transition-colors duration-fast hover:border-primary"
              >
                <LuPaperclip size={22} className="text-primary" aria-hidden />
                <strong className="text-body font-semibold text-fg">{t("appeals.items.fileTitle")}</strong>
                <span className="text-caption">{t("appeals.items.fileHint")}</span>
              </label>
            )}
          </>
        )}
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        {group(t("appeals.items.codes"), w.items.filter((i) => i.tipo === "CODIGO"), true)}
        {group(t("appeals.items.materials"), w.items.filter((i) => i.tipo === "MATERIAL"), false)}
      </div>
    </>
  );
};
